/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import { verifiedSupplierChoices } from "utils/supplierSelection";
import { useMaterialUIController } from "context";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useEffect, useRef, useState } from "react";
import PickDate from "components/DatePicker/DatePicker";
import {
  FiArrowLeft,
  FiEye,
  FiFileText,
  FiTrash2,
  FiUploadCloud,
} from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  addExpensesData,
  getCurrencyCode,
  getExpensesById,
  updateExpenseData,
} from "utils/api";
import { appConstants } from "utils/appConstants";
import { formatCurrencyAUD } from "utils/formatCurrency";
import { getScreenRight, showResMsg, validateField } from "utils/generic";
import { genericMessage } from "utils/genericMessages";
import InvoiceSelect from "components/FormControls/ThemedSelect";
import PageSkeleton from "components/Skeleton/PageSkeleton";
import { expenseReceiptAccept, expenseReceiptError } from "utils/expenseReceipt";
import useUnsavedChanges from "hooks/useUnsavedChanges";
import "layouts/Quotation/quotation.css";
import "./expenseEditor.css";

const emptyExpense = {
  categoryCodeName: "",
  categoryID: "",
  contactTypeId: "",
  name: "",
  expenseDate: new Date(),
  totalAmount: "",
  taxAmount: "",
  tipAmount: "",
  referenceNumber: "",
  notes: "",
  uploadDocument: "",
  businessUserID: JSON.parse(sessionStorage.getItem("LOGIN_DETAILS"))
    ?.businessUserID,
  searchParameterList: [
    "categoryCodeName",
    "name",
    "expenseDate",
    "totalAmount",
    "taxAmount",
    "tipAmount",
    "categoryNameID",
    "contactTypeId",
  ],
};

const ocrValue = (field) =>
  field && typeof field === "object" && "value" in field ? field.value : field;
const ocrNumber = (...fields) => {
  const field = fields.find(
    (candidate) => candidate !== undefined && candidate !== null
  );
  if (ocrValue(field) == null || String(ocrValue(field)).trim() === "") return null;
  const value = Number(String(ocrValue(field) ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(value) ? value : null;
};
const normalizeOcrText = (value) =>
  String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
const parseOcrDate = (field) => {
  const value = ocrValue(field);
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const text = String(value).trim();
  const dayFirst = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/);
  if (dayFirst) {
    const year = Number(
      dayFirst[3].length === 2 ? `20${dayFirst[3]}` : dayFirst[3]
    );
    const parsed = new Date(year, Number(dayFirst[2]) - 1, Number(dayFirst[1]));
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

function AddExpenses() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const fileInput = useRef(null);
  const importedReceipt = useRef(null);
  const receiptRequest = useRef(0);
  const [referenceDataReady, setReferenceDataReady] = useState(false);
  const [values, setValues] = useState(emptyExpense);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [currencyCode, setCurrencyCode] = useState("AUD");
  const [actualFile, setActualFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [viewerOpen, setViewerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(Boolean(id));
  const [canEdit, setCanEdit] = useState(false);
  const [errors, setErrors] = useState({});
  const [taxTreatment, setTaxTreatment] = useState("gst");
  const [receiptImport, setReceiptImport] = useState({
    status: "idle",
    message: "",
    warnings: [],
  });
  const savedSnapshot = useRef({ values: JSON.stringify(values), file: null });
  const isDraft = !id || values.isDraft === true || values.status === "Draft";
  const unsaved = useUnsavedChanges(
    !initialLoading && (JSON.stringify(values) !== savedSnapshot.current.values || actualFile !== savedSnapshot.current.file || loading),
    {
      documentName: "expense",
      saveMode: isDraft ? "draft" : "record",
      isExistingDraft: Boolean(id && isDraft),
      isSaving: loading,
      canSaveDraft: canEdit && receiptImport.status !== "loading",
      onSave: () => save(isDraft, true),
    }
  );

  useEffect(() => {
    let active = true;
    const businessPayload = { businessUserID: values.businessUserID };

    if (id) setInitialLoading(true);
    Promise.all([
      getScreenRight("Expenses").then((right) => {
        if (active) setCanEdit(right === "Full Access");
      }),
      getCurrencyCode(
        businessPayload,
        (data) => {
          if (active && data.Result_Status)
            setCurrencyCode(data.Result_Output || "AUD");
        },
        dispatch
      ),
      axios
        .post(`${appConstants.baseURL}items/fetchCategoriesData`, businessPayload)
        .then((res) => {
          if (active) setCategories(res.data.fetchCategories || []);
        })
        .catch(() => {
          if (active) setCategories([]);
        }),
      axios
        .post(`${appConstants.baseURL}expenses/ContactData`, businessPayload)
        .then((res) => {
          if (active) setSuppliers(verifiedSupplierChoices(res.data.fetchContactType));
        })
        .catch(() => {
          if (active) setSuppliers([]);
        }),
      id
        ? getExpensesById(
            { _id: id },
            (data) => {
              if (!active) return;
              const expense = data?.Result_Output?.[0];
              if (!expense) return;
              const loaded = { ...emptyExpense, ...expense };
              savedSnapshot.current = { values: JSON.stringify(loaded), file: null };
              setValues(loaded);
              if (expense.uploadDocument) {
                setFileName(expense.uploadDocument);
                setFileUrl(
                  `${
                    appConstants.baseURL
                  }expenses/viewDocument?fileName=${encodeURIComponent(
                    expense.uploadDocument
                  )}`
                );
              }
            },
            dispatch
          )
        : Promise.resolve(),
    ])
      .catch(() => undefined)
      .finally(() => {
        if (active) { setInitialLoading(false); setReferenceDataReady(true); }
      });

    return () => {
      active = false;
    };
  }, [dispatch, id]);

  useEffect(() => {
    const receipt = location.state?.receiptFile;
    if (id || !referenceDataReady || !canEdit || !receipt || importedReceipt.current === receipt) return;
    importedReceipt.current = receipt;
    if (selectFile({ target: { files: [receipt] } })) extractReceipt(receipt);
  }, [location.state, referenceDataReady, canEdit, id]);

  useEffect(() => () => { receiptRequest.current += 1; }, []);

  useEffect(
    () => () => {
      if (actualFile && fileUrl) URL.revokeObjectURL(fileUrl);
    },
    [actualFile, fileUrl]
  );

  if (id && initialLoading) {
    return (
      <DashboardLayout>
        <main
          className={`expense-editor${darkMode ? " expense-editor--dark" : ""}`}
        >
          <PageSkeleton
            type="editor"
            variant="expense"
            label="Loading expense for editing…"
          />
        </main>
      </DashboardLayout>
    );
  }

  const update = (name) => (event) => {
    const value = event.target.value;
    setValues((current) => ({ ...current, [name]: value }));
  };
  const total = Number(String(values.totalAmount).replace(/,/g, "")) || 0;
  const tax = Number(String(values.taxAmount).replace(/,/g, "")) || 0;
  const excludingTax = Math.max(0, total - tax);

  const chooseSupplier = (event) => {
    if (event.target.value === "add-new")
      return navigate("/contacts/addContact?new=expense");
    const supplier = suppliers.find((item) => item._id === event.target.value);
    setValues((current) => ({
      ...current,
      contactTypeId: event.target.value,
      name: supplier?.companyName || "",
    }));
    return undefined;
  };
  const chooseCategory = (event) => {
    const category = categories.find((item) => item._id === event.target.value);
    setValues((current) => ({
      ...current,
      categoryID: event.target.value,
      categoryCodeName: category?.categoryCodeName || "",
    }));
  };
  const selectFile = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    const error = expenseReceiptError(selected);
    if (error) { showResMsg(error, 2000, false); return false; }
    receiptRequest.current += 1;
    if (actualFile && fileUrl) URL.revokeObjectURL(fileUrl);
    setActualFile(selected);
    setFileName(selected.name);
    setFileUrl(URL.createObjectURL(selected));
    setReceiptImport({ status: "idle", message: "", warnings: [] });
    return true;
  };
  const removeFile = () => {
    receiptRequest.current += 1;
    if (actualFile && fileUrl) URL.revokeObjectURL(fileUrl);
    setActualFile(null);
    setFileName("");
    setFileUrl("");
    setValues((current) => ({ ...current, uploadDocument: "" }));
    setReceiptImport({ status: "idle", message: "", warnings: [] });
  };

  const extractReceipt = async (receipt = actualFile) => {
    if (!receipt) return;
    const requestID = ++receiptRequest.current;
    setReceiptImport({
      status: "loading",
      message: "Reading receipt details…",
      warnings: [],
    });
    const formData = new FormData();
    formData.append("file", receipt);
    formData.append("language", "en");
    try {
      const response = await fetch("/ocr-api/invoice/extract", {
        method: "POST",
        body: formData,
      });
      const result = await response.json().catch(() => ({}));
      if (requestID !== receiptRequest.current) return;
      if (!response.ok) throw new Error(result.detail || "Receipt extraction failed.");
      const extracted = result.invoice || {};
      const totals = extracted.totals || {};
      const totalAmount = ocrNumber(
        totals.total,
        totals.grandTotal,
        totals.amountDue,
        totals.invoiceTotal
      );
      const taxAmount = ocrNumber(
        totals.gst,
        totals.tax,
        totals.taxTotal,
        totals.totalTax,
        totals.gstAmount
      );
      const supplierName = ocrValue(extracted.supplier?.name);
      const matchedSupplier = suppliers.find((supplier) =>
        [supplier.companyName, supplier.name]
          .map(normalizeOcrText)
          .filter(Boolean)
          .includes(normalizeOcrText(supplierName))
      );
      const parsedDate = parseOcrDate(extracted.invoiceDate);
      const referenceNumber = ocrValue(extracted.invoiceNumber);
      const warnings = Array.isArray(extracted.warnings)
        ? [...extracted.warnings]
        : [];
      if (supplierName && !matchedSupplier)
        warnings.push(
          `Supplier “${supplierName}” was not matched. Select an existing supplier before saving.`
        );
      setValues((current) => ({
        ...current,
        contactTypeId: matchedSupplier?._id || current.contactTypeId,
        name:
          matchedSupplier?.companyName || matchedSupplier?.name || current.name,
        expenseDate:
          parsedDate || current.expenseDate,
        referenceNumber: referenceNumber || current.referenceNumber,
        totalAmount:
          totalAmount !== null ? totalAmount.toFixed(2) : current.totalAmount,
        taxAmount:
          taxAmount !== null ? taxAmount.toFixed(2) : current.taxAmount,
      }));
      setReceiptImport({
        status: "success",
        message:
          "Receipt read successfully. Review the extracted values and select an account before saving.",
        warnings: [...new Set(warnings)],
      });
    } catch (error) {
      if (requestID !== receiptRequest.current) return;
      setReceiptImport({
        status: "error",
        message: error.message || "Receipt extraction failed.",
        warnings: [],
      });
    }
  };

  const save = async (draft = false, leave = false) => {
    if (loading || !canEdit || initialLoading || receiptImport.status === "loading") return false;
    const nextErrors = {};
    if (!draft) {
      validateField(values.name, () => {
        nextErrors.name = "Choose a supplier or merchant.";
      });
      validateField(values.categoryCodeName, () => {
        nextErrors.categoryCodeName = "Choose an account.";
      });
      validateField(values.totalAmount, () => {
        nextErrors.totalAmount = "Enter the total amount.";
      });
      validateField(values.taxAmount, () => {
        nextErrors.taxAmount = "Enter the GST amount, or 0.";
      });
    }
    if (!draft && tax > total)
      nextErrors.taxAmount = "GST cannot exceed the total amount.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return false;
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (value !== "" && value != null) formData.append(key, value);
    });
    formData.set("isDraft", String(draft));
    formData.set("status", draft ? "Draft" : "Saved");
    if (actualFile) {
      formData.append("file", actualFile);
      formData.append("fileName", fileName);
    }
    setLoading(true);
    const request = id ? updateExpenseData : addExpensesData;
    let saved = false;
    let responded = false;
    try {
    await request(
      formData,
      (data) => {
        responded = true;
        if (data.Result_Status) {
          saved = true;
          savedSnapshot.current = { values: JSON.stringify(values), file: actualFile };
          unsaved.markSaved();
          showResMsg(
            draft
              ? "Expense draft saved successfully."
              : id
              ? genericMessage.updated
              : genericMessage.added,
            1000,
            true,
            leave ? undefined : navigate,
            "/expenses"
          );
        }
        else showResMsg(data.Result_Message, 2000, false);
      },
      dispatch
    );
    } catch {
      // Some transports reject; others report failure through the callback.
    } finally {
      setLoading(false);
    }
    if (!responded) showResMsg("Unable to save expense. Please try again.", 2000, false);
    return saved;
  };

  return (
    <DashboardLayout>
      {unsaved.dialog}
      <main
        className={`expense-editor${darkMode ? " expense-editor--dark" : ""}`}
      >
        <button
          className="expense-back"
          type="button"
          onClick={() => navigate("/expenses")}
        >
          <FiArrowLeft />
          Back to Expenses
        </button>
        <header className="expense-editor__header">
          <div>
            <h1>{id ? "Edit expense" : "Add expense"}</h1>
            <p>Enter the purchase once. Attach evidence when you have it.</p>
          </div>
        </header>
        <div className="expense-editor__layout">
          <div className="expense-editor__form">
            <section className="expense-panel">
              <h2>Basic details</h2>
              <div className="expense-grid">
                <label>
                  <span>Supplier / merchant</span>
                    <InvoiceSelect
                      className="expense-theme-select expense-supplier-search"
                      contactType="supplier"
                      placeholder="Search supplier"
                      dark={darkMode}
                      aria-label="Supplier or merchant"
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={errors.name ? "expense-supplier-error" : undefined}
                      value={values.contactTypeId || ""}
                      onChange={chooseSupplier}
                      disabled={Boolean(id)}
                    >
                      <option value="" hidden>Search supplier</option>
                      <option value="add-new">Add new supplier</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier._id} value={supplier._id}>
                          {supplier.companyName}
                        </option>
                      ))}
                    </InvoiceSelect>
                  {errors.name && <small id="expense-supplier-error">{errors.name}</small>}
                </label>
                <PickDate
                  label="Expense date"
                  maxDate={new Date()}
                  selected={
                    values.expenseDate ? new Date(values.expenseDate) : null
                  }
                  onChange={(date) =>
                    setValues((current) => ({
                      ...current,
                      expenseDate: date || new Date(),
                    }))
                  }
                  dateFormat="MMM d, yyyy"
                />
                <label className="expense-grid__wide">
                  <span>Supplier reference / description</span>
                  <input
                    value={values.referenceNumber || ""}
                    maxLength={50}
                    onChange={update("referenceNumber")}
                    placeholder="Purchase description or reference"
                  />
                </label>
                <label>
                  <span>Category / account</span>
                    <InvoiceSelect
                      className="expense-theme-select"
                      dark={darkMode}
                      aria-label="Category or account"
                      value={values.categoryID || ""}
                      onChange={chooseCategory}
                      disabled={Boolean(id)}
                    >
                      <option value="">Select account</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.categoryCodeName}
                        </option>
                      ))}
                    </InvoiceSelect>
                  {errors.categoryCodeName && (
                    <small>{errors.categoryCodeName}</small>
                  )}
                </label>
                <label>
                  <span>Currency</span>
                  <input disabled value={currencyCode || "AUD"} />
                </label>
              </div>
            </section>
            <section className="expense-panel">
              <h2>Amount &amp; GST</h2>
              <div className="expense-grid">
                <label className="expense-grid__wide">
                  <span>Total amount · {currencyCode}</span>
                  <input
                    inputMode="decimal"
                    value={values.totalAmount || ""}
                    onChange={update("totalAmount")}
                    placeholder="0.00"
                  />
                  {errors.totalAmount && <small>{errors.totalAmount}</small>}
                </label>
                <label>
                  <span>Source tax treatment</span>
                    <InvoiceSelect
                      className="expense-theme-select"
                      dark={darkMode}
                      aria-label="Source tax treatment"
                      value={taxTreatment}
                      onChange={(event) => setTaxTreatment(event.target.value)}
                    >
                      <option value="gst">GST inclusive · 10% example</option>
                      <option value="none">No GST</option>
                    </InvoiceSelect>
                </label>
                <label>
                  <span>GST recorded · {currencyCode}</span>
                  <input
                    inputMode="decimal"
                    value={values.taxAmount || ""}
                    onChange={update("taxAmount")}
                    placeholder="0.00"
                  />
                  {errors.taxAmount && <small>{errors.taxAmount}</small>}
                </label>
              </div>
              <div className="expense-info">
                Use the source document. GST recorded is stored separately and
                is not an eligibility assessment.
              </div>
            </section>
            <section className="expense-panel">
              <h2>Payment status</h2>
              <div className="expense-grid">
                <label>
                  <span>Was this expense already paid?</span>
                    <InvoiceSelect
                      className="expense-theme-select"
                      dark={darkMode}
                      aria-label="Payment status"
                      value="Unpaid"
                      disabled
                    >
                      <option>Unpaid</option>
                    </InvoiceSelect>
                </label>
                <label>
                  <span>Payment terms</span>
                    <InvoiceSelect
                      className="expense-theme-select"
                      dark={darkMode}
                      aria-label="Payment terms"
                      value="14 days from expense date"
                      disabled
                    >
                      <option>14 days from expense date</option>
                    </InvoiceSelect>
                </label>
              </div>
              <div className="expense-balance">
                <span>Outstanding on save</span>
                <strong>${formatCurrencyAUD(total)}</strong>
              </div>
              <p className="expense-help">
                Payment updates require a supported expense-payment endpoint.
              </p>
            </section>
            <section className="expense-panel">
              <h2>Receipt</h2>
              <button
                className="expense-upload"
                type="button"
                onClick={() => fileInput.current?.click()}
              >
                <FiUploadCloud />
                <strong>
                  {fileName || "Select a receipt or drop it here"}
                </strong>
                <span>PDF, PNG, JPG · Library pattern · up to 10 MB</span>
              </button>
              <input
                ref={fileInput}
                hidden
                type="file"
                aria-label="Expense receipt"
                accept={expenseReceiptAccept}
                onChange={selectFile}
              />
              {fileName && (
                <div className="expense-file-actions">
                  <button type="button" onClick={() => setViewerOpen(true)}>
                    <FiEye />
                    View
                  </button>
                  <button type="button" onClick={removeFile}>
                    <FiTrash2 />
                    Remove
                  </button>
                  {actualFile && (
                    <button
                      type="button"
                      disabled={receiptImport.status === "loading"}
                      onClick={() => extractReceipt()}
                    >
                      <FiFileText />
                      {receiptImport.status === "loading"
                        ? "Reading…"
                        : "Extract details"}
                    </button>
                  )}
                </div>
              )}
              {receiptImport.status !== "idle" && (
                <div
                  className={`expense-receipt-import is-${receiptImport.status}`}
                  role={receiptImport.status === "error" ? "alert" : "status"}
                >
                  <strong>{receiptImport.message}</strong>
                  {receiptImport.warnings.map((warning) => (
                    <span key={warning}>{warning}</span>
                  ))}
                </div>
              )}
              <p className="expense-help">
                Attachment is independent of saving or payment. You can enter
                the expense manually.
              </p>
            </section>
            <section className="expense-panel">
              <h2>Private internal notes</h2>
              <label>
                <span>Internal note (optional)</span>
                <textarea
                  value={values.notes || ""}
                  maxLength={500}
                  onChange={update("notes")}
                  placeholder="Materials purchased for the September switchboard work."
                />
              </label>
              <p className="expense-help">
                Only visible to your team. Not a supplier message.
              </p>
            </section>
          </div>
          <aside className="expense-editor__aside">
            <section className="expense-panel">
              <h2>Expense total</h2>
              <strong className="expense-total">
                ${formatCurrencyAUD(total)}
              </strong>
              <p>{currencyCode} · Unsaved · GST inclusive example</p>
            </section>
            <section className="expense-panel">
              <h2>Review before saving</h2>
              <dl>
                <div>
                  <dt>Excluding GST</dt>
                  <dd>${formatCurrencyAUD(excludingTax)}</dd>
                </div>
                <div>
                  <dt>GST recorded</dt>
                  <dd>${formatCurrencyAUD(tax)}</dd>
                </div>
                <div>
                  <dt>Total amount</dt>
                  <dd>${formatCurrencyAUD(total)}</dd>
                </div>
              </dl>
            </section>
            <section className="expense-panel expense-save">
              <h2>Unpaid expense</h2>
              <p>
                Recording this expense adds ${formatCurrencyAUD(total)} to
                supplier payables unless payment is confirmed.
              </p>
              <button
                className="expense-primary"
                disabled={!canEdit || loading || receiptImport.status === "loading"}
                aria-busy={loading || undefined}
                type="button"
                onClick={() => save(false)}
              >
                {loading && (
                  <span className="expense-button-spinner" aria-hidden="true" />
                )}
                {loading
                  ? id
                    ? "Updating expense…"
                    : "Saving expense…"
                  : id
                  ? "Update expense"
                  : "Save expense"}
              </button>
              {(!id || values.isDraft) && (
                <button
                  className="expense-secondary"
                  disabled={!canEdit || loading || receiptImport.status === "loading"}
                  type="button"
                  onClick={() => save(true)}
                >
                  Save draft
                </button>
              )}
              <button
                className="expense-secondary"
                disabled={loading}
                type="button"
                onClick={() => navigate("/expenses")}
              >
                Cancel
              </button>
            </section>
          </aside>
        </div>
      </main>
      {viewerOpen && (
        <div
          className="expense-viewer document-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Receipt viewer"
        >
          <div>
            <header>
              <strong>{fileName}</strong>
              <button type="button" onClick={() => setViewerOpen(false)}>
                Close
              </button>
            </header>
            <iframe title="Receipt viewer" src={fileUrl} />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default AddExpenses;
